import React, {
    useContext, useCallback, useMemo, useState, useEffect
} from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import LocalDateUtil from 'gw-portals-util-js/LocalDateUtil';
import { readViewModelValue } from 'gw-jutro-adapters-react';
// eslint-disable-next-line import/no-unresolved
import appConfig from 'app-config';
import { MetadataForm } from '@jutro/uiconfig';
import { useDependencies } from 'gw-portals-dependency-react';
import { withAuthenticationContext } from 'gw-digital-auth-react';
import { TranslatorContext, FormattedMessage } from '@jutro/locale';
import { withApplicationPropertyContext, PaymentComponent } from 'tdic-components-platform-react';
import metadata from './ChangePaymentPlan.metadata.json5';
import styles from './ChangePaymentPlan.module.scss';
import messages from './ChangePaymentPlan.messages';

function ChangePaymentPlan({
    callbackCancelSetupAutomaticPayment,
    callbackConfirmSetupAutomaticPayment, model, prevPaymentInstrumentPlan,
    periodicity, cancellationStatus, isExpired, canChangePaymentPlan, authHeader
}) {
    const translator = useContext(TranslatorContext);
    const { tdic: { paymentPlanCode } } = appConfig;
    const [isTnCAccepted, updateTnCAccepted] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(prevPaymentInstrumentPlan);
    const [emailSubmitted, setEmailSubmitted] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [pageErrorAPW, setPageErrorAPW] = useState(undefined);
    const [isLoadingEmail, setIsLoadingEmail] = useState(false);
    const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
    const [hasExistingEmail, setHasExistingEmail] = useState(false);
    const [emailAddress, setEmailAddress] = useState('');
    const policyNumber = _.get(model, 'policyNumber', null) || _.get(prevPaymentInstrumentPlan, 'policyNumber', null);
    const { AccountBillingDetailsService: BillingService } = useDependencies(
        'AccountBillingDetailsService'
    );
    const [emailValidationError, setEmailValidationError] = useState('');
    const paymentPlanHeader = useCallback(() => {
        const paymentPlan = _.get(selectedPaymentMethod, 'paymentPlan', '');
        return (paymentPlan === paymentPlanCode.monthly ? 'Autopay' : '')
        || (paymentPlan === paymentPlanCode.semiAnnual ? 'Semi Annual' : '')
        || (paymentPlan === paymentPlanCode.annual ? 'Annual' : '');
    }, [paymentPlanCode, selectedPaymentMethod]);

    useEffect(() => {
        setSelectedPaymentMethod(prevPaymentInstrumentPlan);
    }, [prevPaymentInstrumentPlan]);

    const isPaymentPlanChanged = useCallback(() => {
        return _.get(prevPaymentInstrumentPlan, 'paymentPlan', '') !== _.get(selectedPaymentMethod, 'paymentPlan');
    }, [prevPaymentInstrumentPlan, selectedPaymentMethod]);

    const isMonthlyPlanSelectedFromOthers = useCallback(() => {
        return _.get(prevPaymentInstrumentPlan, 'paymentPlan', '') !== _.get(paymentPlanCode, 'monthly')
        && _.get(selectedPaymentMethod, 'paymentPlan', '') === _.get(paymentPlanCode, 'monthly');
    }, [prevPaymentInstrumentPlan, paymentPlanCode, selectedPaymentMethod]);

    const getPaymentPlanText = useCallback(() => {
        return (
            <FormattedMessage
                {...messages.selectPlan}
                values={{
                    each: <div className={styles.divUnderline}>each</div>,
                    note:
    <div className={styles.divBold}>
        <br />
        Please Note
    </div>,
                }}
            />
        );
    }, []);

    const onClickConfirmSetupAutomaticPayment = useCallback((paymentDetailsVm, paymentMethod) => {
        const paymentPlan = _.get(selectedPaymentMethod, 'paymentPlan', '');
        return callbackConfirmSetupAutomaticPayment(paymentDetailsVm, paymentMethod, paymentPlan);
    }, [callbackConfirmSetupAutomaticPayment, selectedPaymentMethod]);

    const getPaymentPlanButton = useCallback(() => {
        const paymentPlan = _.get(selectedPaymentMethod, 'paymentPlan', '');
        if (paymentPlan !== paymentPlanCode.monthly) {
            return translator(messages.setPaymentPlan);
        }
        return translator(messages.setupAutomaticPayments);
    }, [paymentPlanCode, selectedPaymentMethod, translator]);

    const checkEmailValidity = (email) => {
        try {
            if (!email || typeof email !== 'string') {
                return false;
            }
            const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
            return emailRegex.test(email.trim());
        } catch (error) {
            return false;
        }
    };

    const handleEmailChange = useCallback((valueOrEvent) => {
        setEmailAddress(valueOrEvent);
        setEmailValidationError('');
        setPageErrorAPW(undefined);
        setEmailSubmitted(false);
    }, []);

    const isSubmitButtonDisabled = useCallback(() => {
        return isLoadingEmail
            || isSubmittingEmail
            || !emailAddress
            || emailSubmitted;
    }, [isLoadingEmail, isSubmittingEmail, emailAddress, emailSubmitted]);

    const fetchEmailFromService = useCallback(async () => {
        if (!policyNumber) return;
        try {
            setIsLoadingEmail(true);
            const response = await BillingService.getEmailAddress(policyNumber, authHeader);
            if (response && response.trim() !== '') {
                setEmailAddress(response);
                setHasExistingEmail(true); // Email came from backend
                setEmailSubmitted(true); // Mark as submitted since it exists
                if (!checkEmailValidity(response)) {
                    setPageErrorAPW('invalidEmailAPW');
                }
            } else {
                setEmailAddress('');
                setHasExistingEmail(false); // No existing email
                setEmailSubmitted(false);
                setPageErrorAPW('noEmailFound');
            }
        } catch (error) {
            setPageErrorAPW('emailFetchError');
            setEmailAddress('');
            setHasExistingEmail(false);
            setEmailSubmitted(false);
        } finally {
            setIsLoadingEmail(false);
        }
    }, [BillingService, authHeader, policyNumber]);

    const updateSubmitValidEmailAPW = useCallback(async () => {
        setEmailValidationError('');
        setPageErrorAPW(undefined);
        if (!emailAddress || !emailAddress.trim()) {
            setEmailValidationError('invalidEmailFormat');
            return false;
        }
        if (!checkEmailValidity(emailAddress)) {
            setEmailValidationError('invalidEmailFormat');
            return false;
        }
        if (!policyNumber) {
            setPageErrorAPW('policyNumberError');
            return false;
        }
        try {
            setIsSubmittingEmail(true);
            const result = await BillingService.setEmailAddress(policyNumber,
                emailAddress, authHeader);
            if (result) {
                setHasExistingEmail(true);
                setEmailSubmitted(true);
                console.log('Email successfully submitted');
                return true;
            }
            setPageErrorAPW('emailUpdateError');
            return false;
        } catch (error) {
            setPageErrorAPW('emailUpdateError');
            return false;
        } finally {
            setIsSubmittingEmail(false);
        }
    }, [emailAddress, policyNumber, BillingService, authHeader]);

    const history = useHistory('');
    const backToPreviousBillingState = useCallback(() => {
        history.goBack();
    }, [history]);

    const startDate = useMemo(() => {
        return LocalDateUtil.firstDayOfMonth();
    }, []);

    const onRadioButtonChange = useCallback(async (value) => {
        const paymentDataClone = _.cloneDeep(selectedPaymentMethod);
        _.set(paymentDataClone, 'paymentPlan', value);

        if (value === 'Monthly APW (12 Pay)') {
            setSelectedPaymentMethod(paymentDataClone);
            setEmailSubmitted(false);
            await fetchEmailFromService();
        } else {
            _.set(paymentDataClone, 'primaryEmail', '');
            setSelectedPaymentMethod(paymentDataClone);
            setEmailSubmitted(false);
            setHasExistingEmail(false);
            setPageErrorAPW(undefined);
        }

        updateTnCAccepted(false);
    }, [selectedPaymentMethod, fetchEmailFromService, updateTnCAccepted]);

    const overrideProps = {
        '@field': {
            labelPosition: 'left'
        },
        setupAutomaticPaymentComponentId: {
            xCenter: 'bc',
            model,
            nextLabel: getPaymentPlanButton(),
            bankAccountPaymentInstrument: {},
            isFormVisible: isPaymentPlanChanged() && isMonthlyPlanSelectedFromOthers(),
            visible: isPaymentPlanChanged(),
            updateTnCAccepted: updateTnCAccepted,
            disabled: isMonthlyPlanSelectedFromOthers() ? !emailSubmitted : false,
            isDisabled: isMonthlyPlanSelectedFromOthers() ? !emailSubmitted : false,
            enable: isMonthlyPlanSelectedFromOthers() ? emailSubmitted : true,
            isTnCAccepted: isMonthlyPlanSelectedFromOthers() ? (isTnCAccepted && emailSubmitted)
                : isTnCAccepted
        },
        paymentContainerId: {
            visible: isPaymentPlanChanged() && isMonthlyPlanSelectedFromOthers()
        },
        selectPaymentPlan: {
            value: _.get(selectedPaymentMethod, 'paymentPlan'),
            visible: cancellationStatus !== 'canceled' && !isExpired && canChangePaymentPlan
        },
        setupAutomaticPaymentHeaderId: {
            visible: isPaymentPlanChanged(),
            content: translator(messages.enrollAutopay, {
                enrollmentPlan: paymentPlanHeader()
            })
        },
        setupAutomaticPaymentPaymentScheduleId: {
            value: periodicity
        },
        setupAutomaticPaymentStartingDateId: {
            value: startDate
        },
        selectPaymentPlanMessage: {
            visible: isPaymentPlanChanged(),
            content: getPaymentPlanText()
        },
        enterValidEmailAddressAPW: {
            value: emailAddress || '',
            onValueChange: handleEmailChange,
            readOnly: isLoadingEmail || isSubmittingEmail || hasExistingEmail,
            disabled: isLoadingEmail || isSubmittingEmail || hasExistingEmail,
            placeholder: (() => {
                if (hasExistingEmail) return '';
                if (isLoadingEmail) return 'Loading email...';
                return 'Enter a valid email address';
            })(),
            className: hasExistingEmail ? styles.emailReadOnlyAPW : '',
            required: !hasExistingEmail
        },
        submitValidEmailAPW: {
            disabled: isSubmitButtonDisabled() || hasExistingEmail
        },
        enterValidEmailAddressAPWContainer: {
            visible: _.get(selectedPaymentMethod, 'paymentPlan', null) === 'Monthly APW (12 Pay)'
        },
        emailValidationErrorMessage: {
            visible: emailValidationError === 'invalidEmailFormat',
            content: 'Please enter a valid email address',
            className: styles.emailValidationErrorCenter
        }
    };
    const readValue = (id, path) => {
        return readViewModelValue(metadata.componentContent, {}, id, path, overrideProps);
    };

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: {
            paymentpagecomponent: PaymentComponent
        },
        resolveCallbackMap: {
            onClickConfirmSetupAutomaticPayment,
            onClickCancelSetupAutomaticPayment: callbackCancelSetupAutomaticPayment,
            onRadioButtonChange,
            updateSubmitValidEmailAPW,
            handleEmailChange,
            backToPreviousBillingState
        }
    };

    return (
        <MetadataForm
            data={{}}
            uiProps={metadata.componentContent}
            overrideProps={overrideProps}
            classNameMap={resolvers.resolveClassNameMap}
            componentMap={resolvers.resolveComponentMap}
            callbackMap={resolvers.resolveCallbackMap}
            resolveValue={readValue}
        />
    );
}

ChangePaymentPlan.propTypes = {
    callbackConfirmSetupAutomaticPayment: PropTypes.func.isRequired,
    authHeader: PropTypes.shape({}).isRequired,
    callbackCancelSetupAutomaticPayment: PropTypes.func.isRequired,
    model: PropTypes.shape({
        policyNumber: PropTypes.string
    }).isRequired,
    periodicity: PropTypes.string.isRequired,
    prevPaymentInstrumentPlan: PropTypes.shape({
        paymentPlan: PropTypes.string,
        policyNumber: PropTypes.string
    }).isRequired,
    cancellationStatus: PropTypes.string.isRequired,
    isExpired: PropTypes.bool.isRequired,
    canChangePaymentPlan: PropTypes.string.isRequired,
};

export default withRouter(withAuthenticationContext(withApplicationPropertyContext(
    ChangePaymentPlan
)));
